import { useEffect, useState } from 'react';
import { useForum } from '../useForum';
import { Button } from '@/components/ui/button';
import { TextField, TextAreaField, TagInput } from '@/components/forms';
import { glassCardStyle } from '@/components/auth/authStyles';
import { X, PenLine } from 'lucide-react';
import { displaySpaceName } from "../forum-display";

interface CreatePostFormProps {
  subforumId: string;
  onClose: () => void;
}

const CHAR_LIMIT = 300;

export function CreatePostForm({ subforumId, onClose }: CreatePostFormProps) {
  const { createPost, state, getSubforum } = useForum();
  const [selectedSubforumId, setSelectedSubforumId] = useState(subforumId);
  const [subForum, setSubForum] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const tagList = tags.map((t) => t.trim()).filter(Boolean);
    const tagStr = tagList.length > 0 ? '\n\n' + tagList.map((t) => `#${t.replace(/^#/, '')}`).join(' ') : '';
    const finalContent = content.trim() + tagStr || '';

    setSubmitting(true);
    try {
      createPost(selectedSubforumId, title.trim(), finalContent);
      setTitle('');
      setContent('');
      setTags([]);
      setSubForum('');
      setSelectedSubforumId(subforumId);
      onClose();
    } catch (error) {
      console.error('Failed to create post:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const currentSubforum = getSubforum(subforumId);
  const subforums =
    state.subforums.length > 0
      ? state.subforums
      : currentSubforum
        ? [currentSubforum]
        : [{ id: subforumId, name: 'General Discussion', slug: 'general', description: '', postCount: 0, createdAt: '' }];

  return (
    <div
      // Scrolls, and centres the dialog with auto margins rather than
      // align-items. On a phone the form is taller than the screen, and a
      // flex-centred box that overflows loses its top and bottom off-screen
      // where no scroll can reach them, the Post button included. Auto margins
      // centre the same way when it fits (T101026002).
      className="fixed inset-0 z-[100] flex overflow-y-auto p-4"
      style={{ background: 'rgba(5,8,16,0.72)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Create a post"
    >
      <div
        className="relative m-auto w-full max-w-2xl overflow-hidden rounded-2xl"
        style={glassCardStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Electric glow header band */}
        <div
          className="flex items-center justify-between gap-3 px-4 py-5 sm:px-6"
          style={{
            background:
              'linear-gradient(135deg, rgba(var(--acc2-rgb),0.12), rgba(var(--pos-rgb),0.08), rgba(var(--acc2-rgb),0.10))',
            borderBottom: '1px solid var(--landing-glass-border)',
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-xl"
              style={{
                background: 'linear-gradient(135deg, rgba(var(--acc2-rgb),0.25), rgba(var(--pos-rgb),0.2))',
                border: '1px solid rgba(var(--acc2-rgb),0.35)',
              }}
            >
              <PenLine size={18} className="text-[var(--acc2)]" />
            </div>
            <div>
              <h2 className="text-base font-bold tracking-tight" style={{ color: 'var(--landing-text)' }}>
                Create a Post
              </h2>
              <p className="text-[11px]" style={{ color: 'var(--landing-text-muted)' }}>
                Share with the community
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors hover:bg-[rgba(var(--acc2-rgb),0.12)] sm:h-9 sm:w-9"
            style={{ color: 'var(--landing-text-muted)' }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form className="space-y-5 p-4 sm:p-6" onSubmit={handleSubmit}>
          <div className="space-y-1.5">
            <span className="text-[13px] font-semibold leading-none" style={{ color: 'var(--landing-text)' }}>
              Community
            </span>
            <div className="flex flex-wrap gap-2">
              {subforums.map((sf) => {
                const active = selectedSubforumId === sf.id;
                return (
                  <button
                    key={sf.id}
                    type="button"
                    onClick={() => setSelectedSubforumId(sf.id)}
                    className="rounded-xl px-3.5 py-2 text-xs font-semibold transition-all"
                    style={
                      active
                        ? {
                            background: 'linear-gradient(135deg, rgba(var(--acc2-rgb),0.18), rgba(var(--pos-rgb),0.14))',
                            border: '1px solid rgba(var(--acc2-rgb),0.5)',
                            color: 'var(--landing-text)',
                          }
                        : {
                            background: 'var(--landing-surface)',
                            border: '1px solid var(--landing-glass-border)',
                            color: 'var(--landing-text-muted)',
                          }
                    }
                  >
                    {displaySpaceName(sf.name)}
                  </button>
                );
              })}
            </div>
          </div>

          <TextField
            label="Sub Forum"
            value={subForum}
            onChange={setSubForum}
            placeholder="e.g. AI Architecture, Security, Releases..."
            hint="Optional - group this post under a specific sub-section."
          />

          <TextField
            label="Title"
            required
            value={title}
            onChange={setTitle}
            placeholder="An interesting title..."
            maxLength={CHAR_LIMIT}
            action={
              <span className="text-[11px] tabular-nums" style={{ color: 'var(--landing-text-dim)' }}>
                {title.length}/{CHAR_LIMIT}
              </span>
            }
          />

          <TextAreaField
            label="Body"
            value={content}
            onChange={setContent}
            rows={4}
            placeholder="What do you want to share? (optional)"
          />

          <TagInput
            label="Tags"
            value={tags}
            onChange={setTags}
            placeholder="e.g. ai, automation, enterprise"
            hint="Press Enter or + to add each tag."
          />

          <div
            className="flex flex-wrap items-center justify-between gap-3 pt-4"
            style={{ borderTop: '1px solid var(--landing-glass-border)' }}
          >
            <p className="text-xs" style={{ color: 'var(--landing-text-muted)' }}>
              Be kind and constructive.
            </p>
            <div className="flex items-center gap-3">
              <Button type="button" variant="ghost" onClick={onClose} style={{ color: 'var(--landing-text-muted)' }}>
                Cancel
              </Button>
              <button
                type="submit"
                disabled={!title.trim() || submitting}
                className="btn-electric inline-flex h-10 items-center justify-center rounded-xl px-5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? 'Posting...' : 'Post'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
